import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Bot, Send, MessageCircle, Lightbulb, Check, Copy, Download, Loader } from 'lucide-react';
import { toast } from 'sonner';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  suggestions?: string[];
  metadata?: {
    detectedTechnology?: string[];
    riskLevel?: 'low' | 'medium' | 'high';
    complexity?: 'simple' | 'medium' | 'complex';
  };
}

interface RefinedTemplate {
  projectType: string;
  technologies: string[];
  complexity: 'simple' | 'medium' | 'complex';
  estimatedDuration: number;
  mainFeatures: string[];
  risks: {
    description: string;
    mitigation: string;
    priority: 'low' | 'medium' | 'high';
  }[];
  teamSize: number;
  budget?: number;
  milestones: string[];
  keyDependencies: string[];
}

export const AITemplateChat: React.FC<{
  initialPrompt?: string;
  onTemplateGenerated?: (template: RefinedTemplate) => void;
}> = ({ initialPrompt = '', onTemplateGenerated }) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [refinedTemplate, setRefinedTemplate] = useState<RefinedTemplate | null>(null);
  const [conversationPhase, setConversationPhase] = useState<'initial' | 'clarification' | 'refinement' | 'finalization'>('initial');
  const [templateData, setTemplateData] = useState({
    projectDescription: '',
    technologies: [] as string[],
    teamSize: 0,
    budget: 0,
    timeline: 0,
    mainRisks: [] as string[],
    mainFeatures: [] as string[]
  });
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    // Start conversation with initial greeting
    if (initialPrompt) {
      handleInitialPrompt(initialPrompt);
    } else {
      sendInitialGreeting();
    }
  }, []);

  const sendInitialGreeting = () => {
    const greeting: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: `Olá! 👋 Bem-vindo ao AI Template Builder! Sou seu assistente de IA especializado em criar templates de projeto personalizados.

Vou guiá-lo através de algumas perguntas para entender melhor seu projeto e criar um template otimizado.

Comecemos: **Descreva brevemente qual é o seu projeto?**

Exemplos:
- "Aplicativo de e-commerce para venda de eletrônicos"
- "Plataforma SaaS de gestão de tarefas"
- "Sistema de IA para análise de documentos"`,
      timestamp: new Date(),
      suggestions: [
        'Aplicativo mobile',
        'Website corporativo',
        'API REST com IA',
        'Sistema ERP'
      ]
    };
    setMessages([greeting]);
    setConversationPhase('initial');
  };

  const handleInitialPrompt = (prompt: string) => {
    setInput(prompt);
    setMessages([
      {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: prompt,
        timestamp: new Date()
      }
    ]);
    // Simulate sending after a brief delay
    setTimeout(() => handleSendMessage(prompt), 500);
  };

  const handleSendMessage = async (messageContent?: string) => {
    const userMessage = messageContent || input;

    if (!userMessage.trim()) return;

    // Add user message
    const newUserMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newUserMessage]);
    setInput('');
    setIsLoading(true);

    // Simulate AI processing
    await new Promise(resolve => setTimeout(resolve, 2000));

    // Generate AI response based on conversation phase
    let aiResponse: ChatMessage;
    let newPhase = conversationPhase;

    switch (conversationPhase) {
      case 'initial':
        aiResponse = generateInitialResponse(userMessage);
        newPhase = 'clarification';
        break;

      case 'clarification':
        aiResponse = generateClarificationResponse(userMessage);
        newPhase = 'refinement';
        break;

      case 'refinement':
        aiResponse = generateRefinementResponse(userMessage);
        newPhase = 'finalization';
        break;

      case 'finalization':
        aiResponse = generateFinalizationResponse(userMessage);
        generateFinalTemplate(userMessage);
        newPhase = 'finalization';
        break;

      default:
        aiResponse = generateInitialResponse(userMessage);
    }

    setMessages(prev => [...prev, aiResponse]);
    setConversationPhase(newPhase);
    setIsLoading(false);
  };

  const generateInitialResponse = (userInput: string): ChatMessage => {
    // Extract project description
    setTemplateData(prev => ({
      ...prev,
      projectDescription: userInput
    }));

    // Detect technologies from input
    const detectedTechs = detectTechnologies(userInput);
    setTemplateData(prev => ({
      ...prev,
      technologies: detectedTechs
    }));

    return {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: `Ótimo! 🎯 Entendi que você está desenvolvendo um **${extractProjectType(userInput)}**.

Vi que você mencionou algumas tecnologias: ${detectedTechs.join(', ')}.

Agora preciso entender melhor seu contexto:

**1. Qual é o tamanho da sua equipe?**
- Solo (apenas você)
- Pequena (2-3 pessoas)
- Média (4-8 pessoas)
- Grande (9+ pessoas)

**2. Qual é o prazo ideal?**
- Urgente (semanas)
- Normal (1-3 meses)
- Relaxado (3+ meses)`,
      timestamp: new Date(),
      suggestions: ['Solo', 'Pequena', 'Média', 'Grande'],
      metadata: {
        detectedTechnology: detectedTechs,
        complexity: estimateComplexity(userInput),
        riskLevel: estimateRiskLevel(userInput)
      }
    };
  };

  const generateClarificationResponse = (userInput: string): ChatMessage => {
    // Extract team size and timeline
    extractTeamAndTimeline(userInput);

    const mainRisks = identifyRisks(templateData.projectDescription, templateData.technologies);
    setTemplateData(prev => ({
      ...prev,
      mainRisks
    }));

    return {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: `Perfeito! 💡 Com essas informações, identifiquei alguns **riscos potenciais** no seu projeto:

${mainRisks.map((risk, i) => `${i + 1}. **${risk}**`).join('\n')}

Agora vou fazer mais uma pergunta importante:

**Quais são as 3 principais funcionalidades que seu projeto deve ter?**

Seja específico! Exemplos:
- "Autenticação de usuários, integração com Stripe, dashboard de análises"
- "Chat em tempo real, upload de arquivos, notificações push"
- "Processamento de IA, API REST, painel administrativo"`,
      timestamp: new Date(),
      metadata: {
        riskLevel: estimateRiskLevel(templateData.projectDescription),
        complexity: estimateComplexity(templateData.projectDescription)
      }
    };
  };

  const generateRefinementResponse = (userInput: string): ChatMessage => {
    // Extract main features
    const features = extractFeatures(userInput);
    setTemplateData(prev => ({
      ...prev,
      mainFeatures: features
    }));

    // Generate estimated duration based on all data
    const estimatedDays = calculateEstimatedDuration(
      templateData.projectDescription,
      templateData.technologies,
      features,
      templateData.teamSize
    );

    return {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: `Excelente! 🚀 Agora tenho uma visão clara do seu projeto!

**Resumo Inteligente do Seu Projeto:**

📋 **Tipo**: ${extractProjectType(templateData.projectDescription)}
🔧 **Tecnologias**: ${templateData.technologies.join(', ')}
📊 **Complexidade**: ${estimateComplexity(templateData.projectDescription).toUpperCase()}
👥 **Equipe**: ${templateData.teamSize} pessoa(s)
⏱️ **Estimativa**: ~${estimatedDays} dias
⚠️ **Riscos Identificados**: ${templateData.mainRisks.length}
✨ **Features Principais**: ${features.length} identificadas

**Preciso fazer apenas UMA última pergunta:**

**Qual é o seu orçamento estimado para este projeto?** (ou deixe em branco se não sabe)

Isso me ajudará a refinar ainda mais as recomendações de ferramentas e serviços.`,
      timestamp: new Date(),
      suggestions: ['$5,000', '$10,000', '$25,000', 'Sem orçamento definido']
    };
  };

  const generateFinalizationResponse = (userInput: string): ChatMessage => {
    // Extract budget
    const budget = extractBudget(userInput);
    setTemplateData(prev => ({
      ...prev,
      budget
    }));

    return {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: `🎉 **Perfeito! Seu template está pronto!**

Com base em todas as informações coletadas, criei um template personalizado e otimizado para seu projeto.

Vou gerar agora:
- ✅ Estrutura de tarefas detalhada
- ✅ Marcos do projeto (milestones)
- ✅ Análise de riscos com mitigação
- ✅ Recomendações de ferramentas
- ✅ Timeline realista

**Clique em "Gerar Template" para continuar!**`,
      timestamp: new Date()
    };
  };

  const generateFinalTemplate = (finalInput: string) => {
    const template: RefinedTemplate = {
      projectType: extractProjectType(templateData.projectDescription),
      technologies: templateData.technologies,
      complexity: estimateComplexity(templateData.projectDescription),
      estimatedDuration: calculateEstimatedDuration(
        templateData.projectDescription,
        templateData.technologies,
        templateData.mainFeatures,
        templateData.teamSize
      ),
      mainFeatures: templateData.mainFeatures,
      risks: generateRiskMitigations(templateData.mainRisks),
      teamSize: templateData.teamSize,
      budget: templateData.budget || undefined,
      milestones: generateMilestones(templateData.mainFeatures, templateData.teamSize),
      keyDependencies: generateDependencies(templateData.technologies, templateData.mainFeatures)
    };

    setRefinedTemplate(template);
    if (onTemplateGenerated) {
      onTemplateGenerated(template);
    }
  };

  // Helper Functions

  const detectTechnologies = (text: string): string[] => {
    const techKeywords: { [key: string]: string } = {
      react: 'React',
      vue: 'Vue.js',
      angular: 'Angular',
      next: 'Next.js',
      svelte: 'Svelte',
      node: 'Node.js',
      python: 'Python',
      django: 'Django',
      flask: 'Flask',
      fastapi: 'FastAPI',
      java: 'Java',
      spring: 'Spring Boot',
      golang: 'Go',
      rust: 'Rust',
      typescript: 'TypeScript',
      postgresql: 'PostgreSQL',
      mongodb: 'MongoDB',
      mysql: 'MySQL',
      redis: 'Redis',
      docker: 'Docker',
      kubernetes: 'Kubernetes',
      aws: 'AWS',
      gcp: 'Google Cloud',
      azure: 'Azure',
      graphql: 'GraphQL',
      rest: 'REST API',
      stripe: 'Stripe',
      auth0: 'Auth0',
      firebase: 'Firebase',
      ai: 'Artificial Intelligence',
      'machine learning': 'Machine Learning',
      gpt: 'GPT/OpenAI'
    };

    const techs: string[] = [];
    const lowerText = text.toLowerCase();

    Object.entries(techKeywords).forEach(([key, value]) => {
      if (lowerText.includes(key)) {
        techs.push(value);
      }
    });

    return techs.length > 0 ? techs : ['Framework/DB não especificados'];
  };

  const extractProjectType = (text: string): string => {
    const lower = text.toLowerCase();
    if (lower.includes('ecommerce') || lower.includes('loja')) return 'E-commerce Platform';
    if (lower.includes('saas')) return 'SaaS Application';
    if (lower.includes('api')) return 'API/Backend System';
    if (lower.includes('mobile') || lower.includes('app')) return 'Mobile Application';
    if (lower.includes('website') || lower.includes('web')) return 'Web Application';
    if (lower.includes('dashboard')) return 'Admin Dashboard';
    if (lower.includes('ai') || lower.includes('machine')) return 'AI/ML Solution';
    return 'Custom Project';
  };

  const estimateComplexity = (text: string): 'simple' | 'medium' | 'complex' => {
    const lower = text.toLowerCase();
    const complexityScore = (
      (lower.includes('ai') || lower.includes('machine') ? 2 : 0) +
      (lower.includes('payment') || lower.includes('stripe') ? 1 : 0) +
      (lower.includes('real-time') || lower.includes('socket') ? 1 : 0) +
      (lower.includes('complex') || lower.includes('advanced') ? 2 : 0) +
      (lower.includes('simple') || lower.includes('basic') ? -2 : 0)
    );

    if (complexityScore >= 3) return 'complex';
    if (complexityScore >= 1) return 'medium';
    return 'simple';
  };

  const estimateRiskLevel = (text: string): 'low' | 'medium' | 'high' => {
    const lower = text.toLowerCase();
    if (lower.includes('payment') || lower.includes('security') || lower.includes('healthcare')) return 'high';
    if (lower.includes('real-time') || lower.includes('api')) return 'medium';
    return 'low';
  };

  const extractTeamAndTimeline = (text: string) => {
    const lower = text.toLowerCase();
    let teamSize = 1;
    let timeline = 45;

    if (lower.includes('pequena') || lower.includes('small') || lower.includes('2') || lower.includes('3')) teamSize = 2;
    if (lower.includes('média') || lower.includes('medium') || lower.includes('4')) teamSize = 4;
    if (lower.includes('grande') || lower.includes('large') || lower.includes('9')) teamSize = 10;

    if (lower.includes('urgente') || lower.includes('semana')) timeline = 14;
    if (lower.includes('normal') || lower.includes('mês') || lower.includes('month')) timeline = 30;
    if (lower.includes('relaxado') || lower.includes('3 mês')) timeline = 90;

    setTemplateData(prev => ({
      ...prev,
      teamSize,
      timeline
    }));
  };

  const identifyRisks = (description: string, technologies: string[]): string[] => {
    const risks = [];
    const lower = description.toLowerCase();

    if (lower.includes('payment') || lower.includes('stripe')) risks.push('Segurança de pagamentos');
    if (lower.includes('api')) risks.push('Escalabilidade da API');
    if (lower.includes('real-time')) risks.push('Sincronização em tempo real');
    if (lower.includes('ai') || lower.includes('ml')) risks.push('Qualidade do modelo de IA');
    if (lower.includes('multi-tenant')) risks.push('Isolamento de dados');
    if (technologies.length > 5) risks.push('Complexidade técnica alta');

    return risks.length > 0 ? risks : ['Requisitos técnicos a definir'];
  };

  const extractFeatures = (text: string): string[] => {
    // Simple feature extraction - in production, would be more sophisticated
    const featurePatterns = [
      /autenticação|login|auth/i,
      /dashboard|analytics|relatório/i,
      /pagamento|stripe|payment/i,
      /notificação|notification|email/i,
      /upload|file/i,
      /real-time|socket|websocket/i,
      /api|integração/i,
      /móvel|mobile/i,
      /admin/i
    ];

    const features = [];
    featurePatterns.forEach(pattern => {
      if (pattern.test(text)) {
        features.push(pattern.source.split('|')[0].replace(/[|]/g, '').replace(/\//g, '').trim());
      }
    });

    return features.length > 0 ? features : ['Funcionalidades customizadas'];
  };

  const extractBudget = (text: string): number => {
    const match = text.match(/\$?(\d+(?:,\d{3})*(?:\.\d{2})?)/);
    return match ? parseInt(match[1].replace(/,/g, '')) : 0;
  };

  const calculateEstimatedDuration = (
    description: string,
    technologies: string[],
    features: string[],
    teamSize: number
  ): number => {
    let baseDays = 30;

    // Adjust by complexity
    if (estimateComplexity(description) === 'simple') baseDays = 14;
    if (estimateComplexity(description) === 'complex') baseDays = 60;

    // Adjust by team size
    baseDays = Math.ceil(baseDays / Math.max(teamSize / 2, 1));

    // Adjust by features
    baseDays += features.length * 3;

    // Adjust by technologies
    if (technologies.includes('AI/ML')) baseDays += 15;
    if (technologies.includes('Kubernetes')) baseDays += 10;

    return baseDays;
  };

  const generateRiskMitigations = (risks: string[]) => {
    const mitigations = {
      'Segurança de pagamentos': {
        description: 'Segurança de pagamentos',
        mitigation: 'Implementar PCI-DSS compliance, usar tokenização, escolher gateway confiável',
        priority: 'high' as const
      },
      'Escalabilidade da API': {
        description: 'Escalabilidade da API',
        mitigation: 'Implementar cache, usar rate limiting, banco de dados otimizado',
        priority: 'high' as const
      },
      'Sincronização em tempo real': {
        description: 'Sincronização em tempo real',
        mitigation: 'Usar WebSockets ou Server-Sent Events, implementar queue de mensagens',
        priority: 'medium' as const
      },
      'Qualidade do modelo de IA': {
        description: 'Qualidade do modelo de IA',
        mitigation: 'Validação contínua, monitoramento de performance, feedback loop',
        priority: 'high' as const
      },
      'Isolamento de dados': {
        description: 'Isolamento de dados',
        mitigation: 'Implementar row-level security, criptografia, audit logs',
        priority: 'high' as const
      },
      'Complexidade técnica alta': {
        description: 'Complexidade técnica alta',
        mitigation: 'Dividir em sprints, documentação clara, code reviews rigorosos',
        priority: 'medium' as const
      }
    };

    return risks.map(risk => mitigations[risk] || {
      description: risk,
      mitigation: 'Avaliar riscos específicos durante planejamento',
      priority: 'low' as const
    });
  };

  const generateMilestones = (features: string[], teamSize: number): string[] => {
    return [
      'Setup e Arquitetura',
      'Core Features (50%)',
      'Integrações e APIs',
      'Testes e Otimização',
      'Deploy e Launch'
    ];
  };

  const generateDependencies = (technologies: string[], features: string[]): string[] => {
    const deps = new Set<string>();

    if (technologies.some(t => t.includes('Database'))) deps.add('Database Design');
    if (features.some(f => f.includes('Auth'))) deps.add('Authentication System');
    if (technologies.some(t => t.includes('API'))) deps.add('API Design & Documentation');
    if (features.some(f => f.includes('Payment'))) deps.add('Payment Gateway Setup');

    return Array.from(deps);
  };

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg">
              <Bot className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="font-bold text-lg text-gray-900">AI Template Chat</h2>
              <p className="text-xs text-gray-600">
                Fase: <span className="font-semibold text-purple-600 capitalize">{conversationPhase}</span>
              </p>
            </div>
          </div>
          {refinedTemplate && (
            <Badge className="bg-green-100 text-green-800">✓ Template Pronto</Badge>
          )}
        </div>
      </div>

      {/* Messages Container */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={message.id}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-xl lg:max-w-2xl ${
                message.role === 'user'
                  ? 'bg-blue-600 text-white rounded-3xl rounded-tr-none'
                  : 'bg-gray-200 text-gray-900 rounded-3xl rounded-tl-none'
              } p-4 shadow-md`}
            >
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{message.content}</p>

              {/* Metadata Info */}
              {message.metadata && (
                <div className="mt-3 pt-3 border-t border-gray-300 space-y-1">
                  {message.metadata.detectedTechnology && (
                    <div className="text-xs">
                      <span className="font-semibold">Tecnologias:</span> {message.metadata.detectedTechnology.join(', ')}
                    </div>
                  )}
                  {message.metadata.complexity && (
                    <div className="text-xs">
                      <span className="font-semibold">Complexidade:</span> {message.metadata.complexity}
                    </div>
                  )}
                </div>
              )}

              {/* Suggestions */}
              {message.suggestions && message.role === 'assistant' && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {message.suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => handleSendMessage(suggestion)}
                      className="text-xs bg-white text-gray-800 px-3 py-1 rounded-full hover:bg-gray-100 transition-colors font-medium"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}

              <p className="text-xs opacity-70 mt-2">
                {message.timestamp.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-200 text-gray-900 rounded-3xl rounded-tl-none p-4">
              <div className="flex items-center gap-2">
                <Loader className="w-4 h-4 animate-spin" />
                <span className="text-sm">IA está analisando...</span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Template Preview (if generated) */}
      {refinedTemplate && (
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-t border-green-200 p-4 max-h-32 overflow-y-auto">
          <div className="text-sm space-y-2">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-green-900">📋 Template Refinado</div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="text-xs">
                  <Copy className="w-3 h-3 mr-1" />
                  Copiar
                </Button>
                <Button size="sm" className="text-xs bg-green-600 hover:bg-green-700">
                  <Download className="w-3 h-3 mr-1" />
                  Usar Template
                </Button>
              </div>
            </div>
            <div className="text-xs text-gray-700">
              {refinedTemplate.projectType} • {refinedTemplate.complexity.toUpperCase()} • {refinedTemplate.estimatedDuration} dias
            </div>
          </div>
        </div>
      )}

      {/* Input Area */}
      <div className="bg-white border-t border-gray-200 p-4">
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.ctrlKey) {
                handleSendMessage();
              }
            }}
            placeholder="Digite sua resposta aqui... (Ctrl+Enter para enviar)"
            className="resize-none h-12"
            disabled={isLoading}
          />
          <Button
            onClick={() => handleSendMessage()}
            disabled={isLoading || !input.trim()}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Send className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          💡 Dica: Seja específico! Quanto mais detalhes, melhor o template gerado.
        </p>
      </div>
    </div>
  );
};